import styles from "./comments-tab.module.css";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  Dropdown,
  Empty,
  Flex,
  Modal,
  Popover,
  Select,
  Spin,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";

import MarkdownText, { MarkdownEditor } from "@/components/ui/MarkdownText";
import ContextMenu from "@/components/containers/ContextMenu";
import ImageWithPreview from "@/components/ui/ImageWithPreview";
import DownloadLink from "@/components/ui/DowloadLink";
import FileModal from "@/components/modals/FileModal";
import FileLinkModal from "@/components/modals/FileLinkModal";
import { formateISODateToMoment, getFullName, getIconByType } from "@/utils";
import { TASK_COMMENT_REACTIONS, uploadTaskCommentImage } from "@/api/tasks";
import { USER_ROLES } from "@/constants/tasks";

import EditIcon from "@/icons/EditIcon";
import LinkIcon from "@/icons/LinkIcon";
import PaperClipIcon from "@/icons/PaperClipIcon";
import PlusIcon from "@/icons/PlusIcon";
import ReplyIcon from "@/icons/ReplyIcon";
import TrashIcon from "@/icons/TrashIcon";

const SMILE_ICON = "☻";
const ACTION_ICON_STYLE = { fontSize: 16, color: "#e66a25" };

const SORT_STORAGE_KEY = "task-comments-sort";

const getCommentText = (comment) =>
  comment?.body ?? "";

const getQuotedCommentUuid = (comment) =>
  comment?.quoted_comment_uuid;

const getQuotedCommentDeleted = (comment) =>
  Boolean(
    comment?.quoted_comment_deleted,
  );

const getCommentDate = (comment) =>
  comment?.created_at ??
  comment?.updated_at;

const getAuthor = (comment) =>
  comment?.author ??
  {};

const getAuthorName = (author) =>
  getFullName(author.lastname, author.firstname, author.patronymic) ||
  author.name ||
  author.full_name ||
  author.email ||
  "Пользователь";

const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const COMMENT_ROLE_LABELS = {
  [USER_ROLES.RESPONSIBLE_EXECUTOR]: "Исполнитель",
  [USER_ROLES.COEXECUTOR]: "Соисполнитель",
  [USER_ROLES.ACCEPTOR]: "Приемщик",
  [USER_ROLES.AUTHOR]: "Автор",
  [USER_ROLES.UNIT_MANAGER]: "Руководитель РГ",
};

const COMMENT_ROLE_PRIORITY = [
  USER_ROLES.RESPONSIBLE_EXECUTOR,
  USER_ROLES.COEXECUTOR,
  USER_ROLES.ACCEPTOR,
  USER_ROLES.AUTHOR,
  USER_ROLES.UNIT_MANAGER,
];

const getRoleCode = (role) =>
  typeof role === "string"
    ? role
    : role?.code || role?.name || role?.slug || role?.role;

const getCommentRoles = (comment) =>
  comment?.roles ||
  comment?.author?.roles ||
  comment?.employee?.roles ||
  comment?.user?.roles ||
  [];

const getRole = (comment) => {
  const explicitRole =
    comment?.task_role?.name ??
    comment?.role?.name ??
    comment?.role_name ??
    comment?.taskRoleName;

  if (explicitRole) return explicitRole;

  const roles = getCommentRoles(comment).map(getRoleCode).filter(Boolean);
  const priorityRole = COMMENT_ROLE_PRIORITY.find((role) =>
    roles.includes(role),
  );

  return COMMENT_ROLE_LABELS[priorityRole] || "";
};

const getAuthorUuid = (comment) => {
  const author = getAuthor(comment);
  return (
    author.uuid ??
    author.user_uuid ??
    author.employee_uuid ??
    comment?.author_uuid
  );
};

const getUserUuid = (user) =>
  user?.employee_uuid ?? user?.user_uuid ?? user?.uuid;

const isCurrentUserAuthor = (comment, currentUser) => {
  const authorUuid = getAuthorUuid(comment);

  return Boolean(
    authorUuid &&
    (authorUuid === currentUser?.employee_uuid ||
      authorUuid === currentUser?.user_uuid),
  );
};

const canEditComment = (comment, currentUser) => {
  if (comment?.can_edit || comment?.permissions?.edit) return true;

  return isCurrentUserAuthor(comment, currentUser);
};

const getAttachmentUrl = (attachment) =>
  attachment.url ||
  attachment.src ||
  `/api/file/download?uuid=${attachment.uuid}`;

const getAttachmentPreviewUrl = (attachment) =>
  attachment.local_preview_url ||
  attachment.preview_url ||
  attachment.previewSrc ||
  attachment.url ||
  attachment.src ||
  `/api/file/preview?uuid=${attachment.uuid}`;

const getAttachmentMarkdownUrl = (attachment) =>
  attachment.url ||
  attachment.src ||
  (attachment.uuid ? `/api/file/download?uuid=${attachment.uuid}` : "");

const getAttachmentName = (attachment) =>
  attachment.original_name ||
  [attachment.name, attachment.extension].filter(Boolean).join(".") ||
  "file";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];

const isImageAttachment = (attachment) =>
  attachment.type === "image" ||
  attachment.mime_type?.startsWith("image/") ||
  attachment.content_type?.startsWith("image/") ||
  IMAGE_EXTENSIONS.includes(attachment.extension?.toLowerCase());

const getImageMarkdown = (attachment) =>
  `![${getAttachmentName(attachment)}](${getAttachmentMarkdownUrl(attachment)})`;

const insertImageMarkdown = (text, attachment) => {
  const markdown = getImageMarkdown(attachment);
  const prefix = text.trim() ? "\n\n" : "";

  return `${text}${prefix}${markdown}\n\n`;
};

const getQuoteMarkdown = (comment) => {
  const text = getCommentText(comment).trim();

  if (!text) return "";

  return text
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
};

const insertReplyQuoteMarkdown = (text, comment) => {
  const quote = getQuoteMarkdown(comment);

  if (!quote) return text;

  return text.trim() ? `${quote}\n\n${text}` : `${quote}\n\n`;
};

const getImageMarkdownVariants = (attachment) =>
  [
    getAttachmentMarkdownUrl(attachment),
    getAttachmentPreviewUrl(attachment),
    getAttachmentUrl(attachment),
    attachment.local_preview_url,
    attachment.preview_url,
    attachment.previewSrc,
  ]
    .filter(Boolean)
    .map((url) => `![${getAttachmentName(attachment)}](${url})`);

const removeImageMarkdown = (text, attachment) =>
  getImageMarkdownVariants(attachment)
    .reduce((result, markdown) => result.replace(markdown, ""), text)
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const normalizeImageMarkdownUrls = (text, attachments = []) =>
  attachments.reduce((result, attachment) => {
    if (!isImageAttachment(attachment)) return result;

    const stableMarkdown = getImageMarkdown(attachment);

    return getImageMarkdownVariants(attachment).reduce(
      (normalizedText, markdown) =>
        normalizedText.replace(markdown, stableMarkdown),
      result,
    );
  }, text);

const normalizeReaction = (reaction) => {
  const reactionData = TASK_COMMENT_REACTIONS.find(
    ({ emoji, code }) =>
      emoji === reaction.emoji ||
      emoji === reaction.code ||
      code === reaction.emoji ||
      code === reaction.code,
  );
  const users = reaction.users || reaction.reacted_by || [];

  return {
    ...reactionData,
    ...reaction,
    emoji: reaction.emoji || reactionData?.emoji,
    code: reaction.emoji || reaction.code || reactionData?.code,
    name: reaction.name || reactionData?.name,
    users,
    count: reaction.count ?? users.length,
  };
};

const renderActionLabel = (icon, label) => (
  <div className={styles.menuItem}>
    {icon}
    {label}
  </div>
);

const CommentsTab = ({
  data = [],
  pending,
  readOnly,
  isAdmins,
  currentUser,
  onCreate,
  onUpdate,
  onDelete,
  onNavigateToComment,
  onCopyLink,
  onToggleReaction,
  onDeleteAttachment,
  taskUuid,
  taskFileContext,
  units = [],
}) => {
  const composerRef = useRef(null);
  const submitButtonRef = useRef(null);
  const uploadHiddenBtnRef = useRef(null);
  const uploadPreviewUrlsRef = useRef({});
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [draftAttachments, setDraftAttachments] = useState([]);
  const [editingUuid, setEditingUuid] = useState(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [editingAttachments, setEditingAttachments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isSystemFileModalOpen, setIsSystemFileModalOpen] = useState(false);
  const [currentAttachmentFile, setCurrentAttachmentFile] = useState(null);
  const [attachmentTarget, setAttachmentTarget] = useState("draft");
  const [openReactionPickerUuid, setOpenReactionPickerUuid] = useState(null);
  const [sort, setSort] = useState(
    () => localStorage.getItem(SORT_STORAGE_KEY) || "oldFirst",
  );

  useEffect(() => {
    localStorage.setItem(SORT_STORAGE_KEY, sort);
  }, [sort]);

  const sortedComments = useMemo(() => {
    const direction = sort === "newFirst" ? -1 : 1;

    return [...data].sort((a, b) => {
      const first = new Date(getCommentDate(a) || 0).getTime();
      const second = new Date(getCommentDate(b) || 0).getTime();

      return (first - second) * direction;
    });
  }, [data, sort]);

  const handleCreate = async () => {
    const text = normalizeImageMarkdownUrls(draft, draftAttachments).trim();
    if (!text || saving) return;

    setSaving(true);
    try {
      await onCreate(text, replyTo, draftAttachments);
      setDraft("");
      setReplyTo(null);
      setDraftAttachments([]);
    } finally {
      setSaving(false);
    }
  };

  const focusComposer = () => {
    composerRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });

    window.setTimeout(() => {
      composerRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
      submitButtonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 250);
  };

  const addImageAttachment = (attachment, target = "draft") => {
    const attachmentUrl =
      attachment.url ||
      attachment.src ||
      (attachment.uuid ? `/api/file/download?uuid=${attachment.uuid}` : "");
    const normalizedAttachment = {
      ...attachment,
      url: attachmentUrl,
      preview_url:
        attachment.preview_url || attachment.previewSrc || attachmentUrl,
    };

    if (target === "editing") {
      setEditingAttachments((current) => [...current, normalizedAttachment]);
      if (isImageAttachment(normalizedAttachment)) {
        setEditingDraft((current) =>
          insertImageMarkdown(current, normalizedAttachment),
        );
      }
      return;
    }

    setDraftAttachments((current) => [...current, normalizedAttachment]);
    if (isImageAttachment(normalizedAttachment)) {
      setDraft((current) => insertImageMarkdown(current, normalizedAttachment));
    }
  };

  const openAttachmentFileModal = (file, target = "draft") => {
    const sourceFileName =
      file.original_name ||
      [file.name, file.extension].filter(Boolean).join(".") ||
      "Файл из системы";
    const sourceFileNameParts = sourceFileName.split(".");
    const sourceExtension =
      file.extension ||
      (sourceFileNameParts.length > 1 ? sourceFileNameParts.pop() : "");
    const name =
      file.name && file.extension
        ? file.name
        : sourceFileNameParts.join(".") ||
          file.name ||
          sourceFileName ||
          "Файл из системы";

    const isNativeFile = typeof File !== "undefined" && file instanceof File;
    const fileData = isNativeFile ? {} : file;
    const rawFile = isNativeFile ? file : file.rawFile;
    const localPreviewUrl =
      file.local_preview_url ||
      (rawFile?.type?.startsWith("image/")
        ? URL.createObjectURL(rawFile)
        : undefined);

    setCurrentAttachmentFile({
      ...fileData,
      ...file,
      rawFile,
      local_preview_url: localPreviewUrl,
      name,
      extension: sourceExtension,
      task_uuid: taskUuid,
      folder_uuid: taskFileContext?.folder_uuid,
      unit_uuid: taskFileContext?.unit_uuid,
      folder: taskFileContext?.folder,
      additional: false,
      skipSave: true,
      isSystem: Boolean(file.isSystem),
    });
    setAttachmentTarget(target);
    setIsAttachmentModalOpen(true);
  };

  const uploadAttachmentFiles = (files, target = "draft") => {
    const imageFiles = files.filter((file) => file.type?.startsWith("image/"));
    if (!imageFiles.length || uploadingImage) return;

    if (imageFiles.length > 1) {
      message.warning("Можно прикрепить только одно изображение за раз");
    }

    openAttachmentFileModal(imageFiles[0], target);
  };

  const handleDeviceUploadChange = ({ file }, target = "draft") => {
    const { status } = file;

    if (status === "uploading") {
      setUploadingImage(true);
      return;
    }

    if (status === "done") {
      setUploadingImage(false);
      const fileObj = file.response?.files?.[0];

      if (!fileObj) {
        message.error("Файл не найден в ответе сервера");
        return;
      }

      const localPreviewUrl = uploadPreviewUrlsRef.current[file.uid];

      openAttachmentFileModal(
        {
          ...fileObj,
          rawFile: file.originFileObj,
          local_preview_url: localPreviewUrl,
        },
        target,
      );
    } else if (status === "error") {
      setUploadingImage(false);
      message.error(
        `Загрузка файла ${file.name} не удалась. Попробуйте еще раз.`,
      );
    }
  };

  const handleSaveAttachmentFile = async (file) => {
    const savedFile =
      typeof file === "string" ? { shortcut_uuid: file } : file || {};
    const sourceFileUuid =
      currentAttachmentFile?.file_uuid ||
      savedFile.file_uuid ||
      currentAttachmentFile?.uuid ||
      savedFile.uuid;
    const extension = savedFile.extension || currentAttachmentFile?.extension;
    const originalName =
      [savedFile.name || currentAttachmentFile?.name, extension]
        .filter(Boolean)
        .join(".") ||
      savedFile.original_name ||
      currentAttachmentFile?.original_name;
    const isSavedFile = sourceFileUuid;
    const attachment =
      isSavedFile || !currentAttachmentFile?.skipSave
        ? {
            ...currentAttachmentFile,
            ...savedFile,
            uuid: sourceFileUuid,
            shortcut_uuid:
              savedFile.shortcut_uuid ||
              savedFile.shortcut?.uuid ||
              currentAttachmentFile?.shortcut_uuid,
            file_uuid: sourceFileUuid,
            extension,
            original_name: originalName,
            name: savedFile.name || currentAttachmentFile?.name,
            description:
              savedFile.description || currentAttachmentFile?.description,
            url: `/api/file/download?uuid=${sourceFileUuid}`,
            preview_url: `/api/file/download?uuid=${sourceFileUuid}`,
          }
        : await uploadTaskCommentImage(currentAttachmentFile.rawFile, {
            task_uuid: taskUuid,
            name: savedFile.name,
            description: savedFile.description,
          });

    addImageAttachment(attachment, attachmentTarget);
    setCurrentAttachmentFile(null);
    setIsAttachmentModalOpen(false);
  };

  const handleBeforeSaveAttachmentFile = async (payload) => {
    if (
      !currentAttachmentFile?.rawFile ||
      currentAttachmentFile?.uuid ||
      payload.uuid ||
      payload.file_uuid
    ) {
      return payload;
    }

    const uploadedFile = await uploadTaskCommentImage(
      currentAttachmentFile.rawFile,
      {
        task_uuid: taskUuid,
        name: payload.name,
        description: payload.description,
        forceUpload: true,
      },
    );
    const extension = uploadedFile.extension || payload.extension;

    return {
      ...payload,
      ...uploadedFile,
      name: payload.name,
      description: payload.description,
      extension,
      original_name:
        [payload.name, extension].filter(Boolean).join(".") ||
        uploadedFile.original_name,
      uuid: uploadedFile.uuid,
      file_uuid: uploadedFile.file_uuid || uploadedFile.uuid,
      task_uuid: taskUuid,
      folder_uuid: taskFileContext?.folder_uuid,
      unit_uuid: taskFileContext?.unit_uuid,
      folder: taskFileContext?.folder,
      additional: false,
      isSystem: false,
    };
  };

  const handleSaveSystemFileLink = async (fileLink) => {
    if (!isImageAttachment(fileLink)) {
      message.warning("К комментарию можно прикрепить только изображение");
      return;
    }

    openAttachmentFileModal(
      {
        ...fileLink,
        uuid: fileLink.file_uuid || fileLink.uuid,
        isSystem: true,
      },
      attachmentTarget,
    );
    setIsSystemFileModalOpen(false);
  };

  const getAttachmentDropdownMenu = (target = "draft") => ({
    items: [
      { key: "device", label: "Прикрепить изображение с устройства" },
      { key: "system", label: "Прикрепить изображение из системы" },
    ],
    onClick: ({ key }) => {
      setAttachmentTarget(target);

      if (key === "device") {
        uploadHiddenBtnRef.current?.querySelector("input")?.click();
      } else if (key === "system") {
        setIsSystemFileModalOpen(true);
      }
    },
  });

  const renderAttachmentButton = (target = "draft") => (
    <Tooltip title="Прикрепить изображение">
      <Dropdown menu={getAttachmentDropdownMenu(target)} trigger={["click"]}>
        <Button
          size="small"
          icon={<PaperClipIcon className={styles.toolbarIcon} />}
          disabled={saving || uploadingImage}
          loading={uploadingImage}
        />
      </Dropdown>
    </Tooltip>
  );

  const renderHiddenUpload = () => (
    <div
      ref={uploadHiddenBtnRef}
      style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
    >
      <Upload
        name="file"
        action="/api/file/upload"
        accept="image/*"
        showUploadList={false}
        beforeUpload={(file) => {
          if (!file.type?.startsWith("image/")) {
            message.warning(
              "К комментарию можно прикрепить только изображение",
            );
            return Upload.LIST_IGNORE;
          }

          uploadPreviewUrlsRef.current[file.uid] = URL.createObjectURL(file);

          return true;
        }}
        onChange={(info) => handleDeviceUploadChange(info, attachmentTarget)}
      >
        <Button style={{ display: "none" }} />
      </Upload>
    </div>
  );

  const handleEditorPaste = (event, target = "draft") => {
    const files = Array.from(event.clipboardData?.files || []);
    const hasImage = files.some((file) => file.type?.startsWith("image/"));

    if (!hasImage) return;

    event.preventDefault();
    uploadAttachmentFiles(files, target);
  };

  const handleEditorDrop = (event, target = "draft") => {
    const files = Array.from(event.dataTransfer?.files || []);
    const hasImage = files.some((file) => file.type?.startsWith("image/"));

    if (!hasImage) return;

    event.preventDefault();
    uploadAttachmentFiles(files, target);
  };

  const handleRemoveDraftAttachment = (attachment) => {
    Modal.confirm({
      title: "Удалить файл?",
      content: "Файл удалится из вложений и из текста комментария.",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () => {
        setDraftAttachments((current) =>
          current.filter(({ uuid }) => uuid !== attachment.uuid),
        );
        setDraft((current) => removeImageMarkdown(current, attachment));
      },
    });
  };

  const handleRemoveEditingAttachment = (attachment) => {
    Modal.confirm({
      title: "Удалить файл?",
      content: "Файл удалится из вложений.",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () => {
        setEditingAttachments((current) =>
          current.filter(({ uuid }) => uuid !== attachment.uuid),
        );
        setEditingDraft((current) => removeImageMarkdown(current, attachment));
      },
    });
  };

  const handleRemoveCommentAttachment = (comment, attachment) => {
    Modal.confirm({
      title: "Удалить файл?",
      content: "Файл удалится из вложений.",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: async () => {
        await onDeleteAttachment?.(comment.uuid, attachment.uuid);

        if (editingUuid === comment.uuid) {
          setEditingDraft((current) =>
            removeImageMarkdown(current, attachment),
          );
          setEditingAttachments((current) =>
            current.filter(({ uuid }) => uuid !== attachment.uuid),
          );
        }
      },
    });
  };

  const startReply = (comment) => {
    setReplyTo(comment);
    setDraft((current) => insertReplyQuoteMarkdown(current, comment));
  };

  const cancelReply = () => {
    setReplyTo(null);
  };

  const startEditing = (comment) => {
    setEditingUuid(comment.uuid);
    setEditingDraft(getCommentText(comment));
    setEditingAttachments(comment.attachments || comment.files || []);
  };

  const cancelEditing = () => {
    setEditingUuid(null);
    setEditingDraft("");
    setEditingAttachments([]);
  };

  const canAttachFilesToComment = (comment) =>
    isCurrentUserAuthor(comment, currentUser) ||
    (!isAdmins &&
      (comment?.can_attach_files || comment?.permissions?.attach_files));

  const handleUpdate = async (comment) => {
    const text = normalizeImageMarkdownUrls(
      editingDraft,
      editingAttachments,
    ).trim();
    if (!text || saving) return;

    const existingAttachmentUuids = new Set(
      (comment.attachments || comment.files || []).map(({ uuid }) => uuid),
    );
    const newAttachments = editingAttachments.filter(
      ({ uuid }) => !existingAttachmentUuids.has(uuid),
    );
    const allowedNewAttachments = canAttachFilesToComment(comment)
      ? newAttachments
      : [];

    setSaving(true);
    try {
      await onUpdate(comment.uuid, text, allowedNewAttachments);
      cancelEditing();
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (comment) => {
    Modal.confirm({
      title: "Удалить комментарий?",
      okText: "Удалить",
      cancelText: "Отмена",
      okButtonProps: { danger: true },
      onOk: () => onDelete(comment.uuid),
    });
  };

  const renderReactions = (comment) => {
    const reactions = (comment.reactions || []).map(normalizeReaction);

    if (!reactions.length && readOnly) return null;

    return (
      <Flex gap={6} align="center" wrap="wrap" className={styles.reactions}>
        {reactions.map((reaction) => {
          const selected = reaction.users.some(
            (user) => getUserUuid(user) === getUserUuid(currentUser),
          );

          return (
            <Popover
              key={reaction.code}
              trigger="hover"
              placement="bottomLeft"
              content={
                <Flex vertical gap={8} className={styles.reactionUsersPopover}>
                  {reaction.users.map((user) => {
                    const userName = getAuthorName(user);

                    return (
                      <Flex
                        align="center"
                        gap={8}
                        key={`${reaction.code}-${getUserUuid(user)}`}
                      >
                        <Avatar src={user.avatar} size={28}>
                          {getInitials(userName)}
                        </Avatar>
                        <Typography.Text>{userName}</Typography.Text>
                      </Flex>
                    );
                  })}
                </Flex>
              }
            >
              <Button
                size="small"
                className={`${styles.reactionChip} ${
                  selected ? styles.reactionChipSelected : ""
                }`}
                disabled={readOnly}
                onClick={() => onToggleReaction?.(comment.uuid, reaction.emoji)}
              >
                <span>{reaction.emoji}</span>
                <span>{reaction.count}</span>
              </Button>
            </Popover>
          );
        })}

        {!readOnly && (
          <Popover
            trigger="click"
            placement="topLeft"
            open={openReactionPickerUuid === comment.uuid}
            onOpenChange={(open) =>
              setOpenReactionPickerUuid(open ? comment.uuid : null)
            }
            content={
              <div className={styles.reactionPicker}>
                {TASK_COMMENT_REACTIONS.map((reaction) => (
                  <Tooltip title={reaction.name} key={reaction.code}>
                    <Button
                      type="text"
                      className={styles.reactionPickerButton}
                      onClick={() => {
                        onToggleReaction?.(comment.uuid, reaction.emoji);
                        setOpenReactionPickerUuid(null);
                      }}
                    >
                      {reaction.emoji}
                    </Button>
                  </Tooltip>
                ))}
              </div>
            }
          >
            <Button
              type="text"
              size="small"
              className={styles.addReactionButton}
            >
              {SMILE_ICON}
            </Button>
          </Popover>
        )}
      </Flex>
    );
  };

  const renderAttachments = (attachments, onRemove) => {
    if (!attachments?.length) return null;

    return (
      <Flex gap={8} wrap="wrap" className={styles.attachments}>
        {attachments.map((attachment) => {
          const attachmentName = getAttachmentName(attachment);

          return (
            <div className={styles.attachment} key={attachment.uuid}>
              {isImageAttachment(attachment) ? (
                <ImageWithPreview
                  uuid={attachment.uuid}
                  original_name={attachmentName}
                  name={attachment.name}
                  extension={attachment.extension}
                  src={getAttachmentPreviewUrl(attachment)}
                  previewSrc={getAttachmentPreviewUrl(attachment)}
                  downloadHref={getAttachmentUrl(attachment)}
                  className={styles.attachmentPreview}
                >
                  <img
                    src={getAttachmentPreviewUrl(attachment)}
                    alt={attachmentName}
                    className={styles.attachmentImage}
                    onError={(event) => {
                      if (event.currentTarget.dataset.fallbackApplied) return;

                      event.currentTarget.dataset.fallbackApplied = "true";
                      event.currentTarget.src = getAttachmentUrl(attachment);
                    }}
                  />
                </ImageWithPreview>
              ) : (
                <DownloadLink
                  href={getAttachmentUrl(attachment)}
                  original_name={attachmentName}
                >
                  <span className={styles.attachmentFileIcon}>
                    {getIconByType(
                      attachment.type,
                      attachment.extension,
                      attachment.element_type,
                    )}
                  </span>
                </DownloadLink>
              )}
              <Typography.Text ellipsis className={styles.attachmentName}>
                {attachmentName}
              </Typography.Text>
              {onRemove && (
                <Button
                  type="text"
                  size="small"
                  className={styles.attachmentRemove}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemove(attachment);
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          );
        })}
      </Flex>
    );
  };

  const renderComment = (comment) => {
    const author = getAuthor(comment);
    const authorName = getAuthorName(author);
    const role = getRole(comment);
    const sourceCommentUuid = getQuotedCommentUuid(comment);
    const sourceComment = data.find(({ uuid }) => uuid === sourceCommentUuid);
    const isSourceCommentDeleted =
      getQuotedCommentDeleted(comment) ||
      Boolean(sourceCommentUuid && !sourceComment);
    const sourceCommentLabel =
      sourceComment && !isSourceCommentDeleted
        ? `В ответ на комментарий: ${getAuthorName(getAuthor(sourceComment))}`
        : "В ответ на удаленный комментарий";
    const attachments = comment.attachments || comment.files || [];
    const isEditing = editingUuid === comment.uuid;
    const isEdited =
      comment?.is_edited ||
      (comment?.updated_at && comment?.created_at !== comment?.updated_at);
    const renderCommentDate = (className = "") => (
      <span className={`${styles.date} ${className}`.trim()}>
        {formateISODateToMoment(getCommentDate(comment))}
        {isEdited && " · изменён"}
      </span>
    );
    const canEdit = canEditComment(comment, currentUser);
    const canManage = canEdit || isAdmins;
    const canAttachFiles = canAttachFilesToComment(comment);
    const canDelete =
      comment?.can_delete ||
      comment?.permissions?.delete ||
      canEdit ||
      isAdmins;
    const topActionItems = [
      {
        key: "copy-link",
        label: renderActionLabel(
          <LinkIcon style={ACTION_ICON_STYLE} />,
          "Скопировать ссылку",
        ),
        onClick: () => onCopyLink?.(comment.uuid),
      },
      ...(!readOnly
        ? [
            {
              key: "reply",
              label: renderActionLabel(
                <ReplyIcon style={ACTION_ICON_STYLE} />,
                "Ответить",
              ),
              onClick: () => startReply(comment),
            },
          ]
        : []),
      ...(canManage && !readOnly
        ? [
            {
              key: "edit",
              label: renderActionLabel(
                <EditIcon style={ACTION_ICON_STYLE} />,
                "Редактировать",
              ),
              onClick: () => startEditing(comment),
            },
          ]
        : []),
    ];
    const actionItems = [
      ...topActionItems,
      ...(canDelete && !readOnly && topActionItems.length
        ? [{ key: "delete-divider", type: "divider" }]
        : []),
      ...(canDelete && !readOnly
        ? [
            {
              key: "delete",
              label: renderActionLabel(
                <TrashIcon style={ACTION_ICON_STYLE} />,
                "Удалить",
              ),
              onClick: () => confirmDelete(comment),
            },
          ]
        : []),
    ];

    return (
      <Card
        className={styles.comment}
        id={`comment-${comment.uuid}`}
        key={comment.uuid}
      >
        <Flex vertical gap={10}>
          <Flex
            justify="space-between"
            gap={12}
            className={styles.commentHeader}
          >
            <Flex
              gap={10}
              align={role ? "flex-start" : "center"}
              className={styles.author}
            >
              <Avatar src={author.avatar} size={32} className={styles.avatar}>
                {getInitials(authorName)}
              </Avatar>
              <Flex vertical className={styles.author}>
                <Typography.Text strong className={styles.authorName}>
                  {authorName}
                </Typography.Text>
                {role && <span className={styles.role}>{role}</span>}
                {renderCommentDate(styles.mobileDate)}
              </Flex>
            </Flex>

            <Flex gap={8} align="center">
              {renderCommentDate(styles.desktopDate)}
              {!!actionItems.length && <ContextMenu items={actionItems} />}
            </Flex>
          </Flex>

          {isEditing ? (
            <Flex vertical gap={8} className={styles.editorContent}>
              <MarkdownEditor
                value={editingDraft}
                onChange={setEditingDraft}
                disabled={saving || uploadingImage}
                rows={4}
                autoFocus
                onPaste={
                  canAttachFiles
                    ? (event) => handleEditorPaste(event, "editing")
                    : undefined
                }
                onDrop={
                  canAttachFiles
                    ? (event) => handleEditorDrop(event, "editing")
                    : undefined
                }
                onDragOver={
                  canAttachFiles ? (event) => event.preventDefault() : undefined
                }
                extraToolbar={
                  canAttachFiles ? renderAttachmentButton("editing") : null
                }
              />
              {renderAttachments(
                editingAttachments,
                canManage && !readOnly
                  ? (attachment) => {
                      if (
                        attachments.some(({ uuid }) => uuid === attachment.uuid)
                      ) {
                        handleRemoveCommentAttachment(comment, attachment);
                        return;
                      }

                      handleRemoveEditingAttachment(attachment);
                    }
                  : null,
              )}
              <Flex justify="space-between" gap={8}>
                <Button
                  danger
                  onClick={() => confirmDelete(comment)}
                  disabled={saving || !canDelete}
                >
                  Удалить
                </Button>
                <Flex gap={8}>
                  <Button onClick={cancelEditing} disabled={saving}>
                    Отмена
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => handleUpdate(comment)}
                    loading={saving}
                    disabled={!editingDraft.trim()}
                  >
                    Сохранить
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            <div className={styles.content}>
              {(sourceCommentUuid || isSourceCommentDeleted) && (
                sourceCommentUuid ? (
                  <button
                    type="button"
                    className={styles.sourceLink}
                    onClick={() => onNavigateToComment?.(sourceCommentUuid)}
                  >
                    {sourceCommentLabel}
                  </button>
                ) : (
                  <span
                    className={`${styles.sourceLink} ${styles.sourceLinkDisabled}`}
                  >
                    {sourceCommentLabel}
                  </span>
                )
              )}
              <MarkdownText value={getCommentText(comment)} />
              {renderAttachments(attachments)}
              {renderReactions(comment)}
            </div>
          )}
        </Flex>
      </Card>
    );
  };

  return (
    <Flex vertical gap={15} className={styles.root}>
      <Flex
        justify="space-between"
        align="center"
        gap={10}
        className={styles.toolbar}
      >
        <Typography.Text type="secondary">
          {data.length ? `Комментариев: ${data.length}` : ""}
        </Typography.Text>
        <Flex gap={8} align="center" className={styles.toolbarActions}>
          <Select
            value={sort}
            onChange={setSort}
            style={{ width: 190 }}
            options={[
              { value: "oldFirst", label: "Сначала старые" },
              { value: "newFirst", label: "Сначала новые" },
            ]}
          />
          {!readOnly && (
            <Tooltip title="Написать комментарий">
              <Button
                type="primary"
                icon={<PlusIcon />}
                onClick={focusComposer}
                aria-label="Написать комментарий"
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {pending ? (
        <div className="spin" style={{ height: 100 }}>
          <Spin size="large" />
        </div>
      ) : sortedComments.length ? (
        <Flex vertical gap={10} className={styles.list}>
          {sortedComments.map(renderComment)}
        </Flex>
      ) : (
        <div className={styles.empty}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}

      {!readOnly && (
        <Card className={styles.composer} ref={composerRef}>
          <Flex vertical gap={10}>
            {replyTo && (
              <Flex
                justify="space-between"
                gap={10}
                className={styles.replyNotice}
              >
                <Typography.Text type="secondary">
                  Ответ на комментарий: {getAuthorName(getAuthor(replyTo))}
                </Typography.Text>
                <Button type="link" size="small" onClick={cancelReply}>
                  Отменить
                </Button>
              </Flex>
            )}
            <MarkdownEditor
              value={draft}
              onChange={setDraft}
              disabled={saving || uploadingImage}
              rows={4}
              onPaste={handleEditorPaste}
              onDrop={handleEditorDrop}
              onDragOver={(event) => event.preventDefault()}
              extraToolbar={renderAttachmentButton()}
            />
            {renderAttachments(draftAttachments, handleRemoveDraftAttachment)}
            <Flex justify="flex-end">
              <Button
                ref={submitButtonRef}
                type="primary"
                style={{ scrollMarginBottom: 20 }}
                onClick={handleCreate}
                loading={saving}
                disabled={!draft.trim()}
              >
                Отправить
              </Button>
            </Flex>
          </Flex>
        </Card>
      )}

      {renderHiddenUpload()}

      {isAttachmentModalOpen && (
        <FileModal
          data={currentAttachmentFile}
          visible={isAttachmentModalOpen}
          units={units}
          disableUnit
          disableFolder
          skipSave={currentAttachmentFile?.skipSave}
          beforeSave={handleBeforeSaveAttachmentFile}
          onSave={handleSaveAttachmentFile}
          onCancel={() => {
            setCurrentAttachmentFile(null);
            setIsAttachmentModalOpen(false);
          }}
        />
      )}

      {isSystemFileModalOpen && (
        <FileLinkModal
          visible={isSystemFileModalOpen}
          onSave={handleSaveSystemFileLink}
          onCancel={() => setIsSystemFileModalOpen(false)}
          isAdmins={isAdmins}
        />
      )}
    </Flex>
  );
};

export default CommentsTab;
